
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Page } from './types';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { CartProvider } from './contexts/CartContext';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { usePageMeta } from './hooks/usePageMeta';
import Header from './components/Header';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import ErrorBoundary from './components/ErrorBoundary';
import { Analytics } from '@vercel/analytics/react';

// Lazy Load Pages
const Home = lazy(() => import('./pages/Home'));
const Shop = lazy(() => import('./pages/Shop'));
const Blog = lazy(() => import('./pages/Blog'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const ReturnsPolicy = lazy(() => import('./pages/ReturnsPolicy'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const DMCAPolicy = lazy(() => import('./pages/DMCAPolicy'));

const LoadingScreen = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-[#F9F7F2]">
    <div className="size-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
    <p className="font-display font-bold text-earth animate-pulse">Squeaky Town is loading...</p>
  </div>
);

interface AppRoutesProps {
  currentPage: Page;
  setCurrentPage: (page: Page) => void;
  shopCategory: string;
  setShopCategory: (cat: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedProductId: number | null;
  setSelectedProductId: (id: number) => void;
  selectedBlogId: number | null;
  setSelectedBlogId: (id: number) => void;
  contactSubject: string;
  setContactSubject: (subject: string) => void;
}

const AppRoutes: React.FC<AppRoutesProps> = ({
  currentPage,
  setCurrentPage,
  shopCategory,
  setShopCategory,
  searchTerm,
  setSearchTerm,
  selectedProductId,
  setSelectedProductId,
  selectedBlogId,
  setSelectedBlogId,
  contactSubject,
  setContactSubject,
}) => {
  const { t } = useLanguage();
  const { settings, isLoading: isSettingsLoading } = useSettings();
  const { isAuthenticated, isAuthChecking } = useAuth();

  // Use custom hook for Title and Favicon
  usePageMeta({ currentPage, t, settings });

  const handleNavigateShop = (category: string = 'Tất cả', search: string = '') => {
    setShopCategory(category);
    setSearchTerm(search);
    setCurrentPage(Page.SHOP);
  };

  const handleProductClick = (id: number) => {
    setSelectedProductId(id);
    setCurrentPage(Page.PRODUCT_DETAIL);
  };

  const handleBlogClick = (id: number) => {
    setSelectedBlogId(id);
    setCurrentPage(Page.BLOG_POST);
  };

  const handleContactNavigate = (subject: string = '') => {
    setContactSubject(subject);
    setCurrentPage(Page.CONTACT);
  };

  const handleAdminSuccess = () => {
    // AuthContext handles the state update, we just redirect
    setCurrentPage(Page.ADMIN_DASHBOARD);
  };

  const handleCheckout = (summary: string) => {
    handleContactNavigate(summary);
  };

  if (isAuthChecking || isSettingsLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex flex-col min-h-screen bg-white text-slate-900">
      <Header
        currentPage={currentPage}
        onNavigate={setCurrentPage}
        onSearch={(term) => handleNavigateShop('Tất cả', term)}
      />
      <CartDrawer onCheckout={handleCheckout} />
      <main className="flex-grow w-full">
        <Suspense fallback={<LoadingScreen />}>
          {(() => {
            switch (currentPage) {
              case Page.HOME: return <Home navigate={setCurrentPage} onProductClick={handleProductClick} onBlogClick={handleBlogClick} />;
              case Page.SHOP: return <Shop initialCategory={shopCategory} searchTerm={searchTerm} onProductClick={handleProductClick} onContactClick={handleContactNavigate} />;
              case Page.BLOG: return <Blog onBlogClick={handleBlogClick} onNavigateShop={handleNavigateShop} />;
              case Page.ABOUT: return <About navigate={setCurrentPage} navigateShop={handleNavigateShop} />;
              case Page.CONTACT: return <Contact initialSubject={contactSubject} />;
              case Page.SHIPPING_POLICY: return <ShippingPolicy />;
              case Page.RETURN_POLICY: return <ReturnsPolicy />;
              case Page.PRODUCT_DETAIL: return <ProductDetail productId={selectedProductId} navigateShop={handleNavigateShop} onOrderClick={handleContactNavigate} onNavigateHome={() => setCurrentPage(Page.HOME)} onProductClick={handleProductClick} />;
              case Page.BLOG_POST: return <BlogPost blogId={selectedBlogId} navigate={setCurrentPage} onBlogClick={handleBlogClick} onNavigateShop={handleNavigateShop} />;
              case Page.ADMIN_LOGIN: return <AdminLogin onLoginSuccess={handleAdminSuccess} />;
              case Page.ADMIN_DASHBOARD: return isAuthenticated ? <AdminDashboard /> : <AdminLogin onLoginSuccess={handleAdminSuccess} />;
              case Page.DMCA: return <DMCAPolicy />;
              default: return <Home navigate={setCurrentPage} onProductClick={handleProductClick} onBlogClick={handleBlogClick} />;
            }
          })()}
        </Suspense>
      </main>
      <Footer
        onNavigate={setCurrentPage}
        onNavigateShop={handleNavigateShop}
      />
    </div>
  );
};

const AppContent: React.FC = () => {
  // Initialize from URL
  const [currentPage, setCurrentPage] = useState<Page>(() => {
    const path = window.location.pathname;
    if (path.startsWith('/product/')) return Page.PRODUCT_DETAIL;
    if (path.startsWith('/blog/') && path !== '/blog') return Page.BLOG_POST;
    if (path === '/shop') return Page.SHOP;
    if (path === '/about') return Page.ABOUT;
    if (path === '/contact') return Page.CONTACT;
    if (path === '/shipping-policy') return Page.SHIPPING_POLICY;
    if (path === '/return-policy') return Page.RETURN_POLICY;
    if (path === '/admin') return Page.ADMIN_DASHBOARD;
    if (path === '/dmca') return Page.DMCA;

    const params = new URLSearchParams(window.location.search);
    let page = params.get('page') as string;
    
    // Handle legacy URLs for SEO
    if (page === 'shipping') page = Page.SHIPPING_POLICY;
    if (page === 'return') page = Page.RETURN_POLICY;
    if (page === 'product_detail') return Page.PRODUCT_DETAIL;
    if (page === 'blog_post') return Page.BLOG_POST;
    
    return Object.values(Page).includes(page as Page) ? (page as Page) : Page.HOME;
  });

  const [shopCategory, setShopCategory] = useState<string>('Tất cả');
  
  const [selectedProductId, setSelectedProductId] = useState<number | null>(() => {
    const path = window.location.pathname;
    if (path.startsWith('/product/')) {
       const id = parseInt(path.split('/')[2]);
       if (!isNaN(id)) return id;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('page') === 'product_detail' && params.has('id')) {
        return parseInt(params.get('id')!) || null;
    }
    return null;
  });

  const [selectedBlogId, setSelectedBlogId] = useState<number | null>(() => {
    const path = window.location.pathname;
    if (path.startsWith('/blog/') && path !== '/blog') {
       const id = parseInt(path.split('/')[2]);
       if (!isNaN(id)) return id;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('page') === 'blog_post' && params.has('id')) {
        return parseInt(params.get('id')!) || null;
    }
    return null;
  });

  const [contactSubject, setContactSubject] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Handle Browser Back Button
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path.startsWith('/product/')) {
        const id = parseInt(path.split('/')[2]);
        setSelectedProductId(isNaN(id) ? null : id);
        setCurrentPage(Page.PRODUCT_DETAIL);
        return;
      }
      if (path.startsWith('/blog/') && path !== '/blog') {
        const id = parseInt(path.split('/')[2]);
        setSelectedBlogId(isNaN(id) ? null : id);
        setCurrentPage(Page.BLOG_POST);
        return;
      }

      const pagePathMap: Record<string, Page> = {
        '/shop': Page.SHOP,
        '/about': Page.ABOUT,
        '/contact': Page.CONTACT,
        '/shipping-policy': Page.SHIPPING_POLICY,
        '/return-policy': Page.RETURN_POLICY,
        '/admin': Page.ADMIN_DASHBOARD,
        '/dmca': Page.DMCA,
        '/': Page.HOME
      };

      if (pagePathMap[path]) {
        setCurrentPage(pagePathMap[path]);
        return;
      }

      const params = new URLSearchParams(window.location.search);
      let page = params.get('page') as string;
      
      // Handle legacy URLs for SEO
      if (page === 'shipping') page = Page.SHIPPING_POLICY;
      if (page === 'return') page = Page.RETURN_POLICY;
      
      setCurrentPage(Object.values(Page).includes(page as Page) ? (page as Page) : Page.HOME);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Sync State to URL
  useEffect(() => {
    let newPath = '/';
    const params = new URLSearchParams(window.location.search);
    
    switch (currentPage) {
      case Page.HOME: newPath = '/'; break;
      case Page.SHOP: newPath = '/shop'; break;
      case Page.BLOG: newPath = '/blog'; break;
      case Page.ABOUT: newPath = '/about'; break;
      case Page.CONTACT: newPath = '/contact'; break;
      case Page.SHIPPING_POLICY: newPath = '/shipping-policy'; break;
      case Page.RETURN_POLICY: newPath = '/return-policy'; break;
      case Page.ADMIN_DASHBOARD: 
      case Page.ADMIN_LOGIN:
        newPath = '/admin'; break;
      case Page.DMCA: newPath = '/dmca'; break;
      case Page.PRODUCT_DETAIL: 
        if (selectedProductId) newPath = `/product/${selectedProductId}`;
        else newPath = '/shop';
        break;
      case Page.BLOG_POST:
        if (selectedBlogId) newPath = `/blog/${selectedBlogId}`;
        else newPath = '/blog';
        break;
    }

    params.delete('page');
    params.delete('id');

    const searchStr = params.toString();
    
    let shouldUpdate = false;
    
    if (currentPage === Page.PRODUCT_DETAIL && selectedProductId) {
      if (!window.location.pathname.startsWith(`/product/${selectedProductId}`)) {
         shouldUpdate = true;
      }
    } else if (currentPage === Page.BLOG_POST && selectedBlogId) {
      if (!window.location.pathname.startsWith(`/blog/${selectedBlogId}`)) {
         shouldUpdate = true;
      }
    } else {
      if (window.location.pathname !== newPath) {
         shouldUpdate = true;
      }
    }
    
    if (shouldUpdate || window.location.search !== (searchStr ? '?' + searchStr : '')) {
      const finalUrl = shouldUpdate ? `${newPath}${searchStr ? '?' + searchStr : ''}` : `${window.location.pathname}${searchStr ? '?' + searchStr : ''}`;
      window.history.pushState({}, '', finalUrl);
      window.scrollTo(0, 0); // Need to scroll to top occasionally? Let's keep it here except we only scroll if page changed
      // Note: We might want to handle scroll differently if it replaces state a lot,
      // but pushState implies a page navigation, so scrolling to top makes sense.
    }
  }, [currentPage, selectedProductId, selectedBlogId, searchTerm, shopCategory]);

  return (
    <AuthProvider currentPage={currentPage} onNavigate={setCurrentPage}>
      <AppRoutes
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        shopCategory={shopCategory}
        setShopCategory={setShopCategory}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedProductId={selectedProductId}
        setSelectedProductId={setSelectedProductId}
        selectedBlogId={selectedBlogId}
        setSelectedBlogId={setSelectedBlogId}
        contactSubject={contactSubject}
        setContactSubject={setContactSubject}
      />
    </AuthProvider>
  );
};


const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <CartProvider>
          <SettingsProvider>
            <AppContent />
            <Analytics />
          </SettingsProvider>
        </CartProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
};

export default App;
